import { type ClassAttributes, type InputHTMLAttributes } from 'react'
import { Link } from 'react-router-dom'
import { useSettings } from '~/@mid_settings/settings'
import { Text } from '~/@mid_ui/typography/Text'
import CompoundingYieldIcon from '~/@mid_ui/icons/CompoundingYieldIcon'
import { FeeImmunityIcon } from '~/@mid_ui/icons/FeeImmunityIcon'
import { TextTooltip } from '~/@mid_ui/text-tooltip/TextTooltip'
import { RevenueFeeTooltip } from '../revenue-fee-tooltip/RevenueFeeTooltip'
import { cn } from '~/shadcn/lib/utils'
import { useFeeImmunity } from '~/@pages_investing/fee-immunity/useFeeImmunity'
import { formatYieldValue } from '~/@pages_investing/fee-immunity/feeImmunityDisplay.utils'
import type { YieldRange } from '~/@pages_investing/fee-immunity/feeImmunity.type'

type Props = ClassAttributes<HTMLInputElement> &
  InputHTMLAttributes<HTMLInputElement> &
  YieldRange & {
    minimumValueError?: string
    availableFundsExceeded: boolean
    usedFutureRevenueFixed: number
    revenueFeeApplied: number
    totalYieldPerAnnum?: number
    isCompoundingAnnualy?: boolean
  }

export const InvestmentInput = ({
  minimumValueError,
  availableFundsExceeded,
  usedFutureRevenueFixed,
  revenueFeeApplied,
  totalYieldPerAnnum,
  isCompoundingAnnualy,
  ...props
}: Props) => {
  const { getLandingPageLink } = useSettings()
  const { hasFeeImmunity } = useFeeImmunity()

  const feeImmunityNumericValue =
    totalYieldPerAnnum != null
      ? formatYieldValue(totalYieldPerAnnum)
      : undefined

  const feeImmunityBadge = feeImmunityNumericValue != null && (
    <div
      className="inline-flex items-center gap-1 rounded bg-purple text-white text-sm select-none px-1.5 py-0.5"
      translate="no">
      <FeeImmunityIcon className="size-4 shrink-0" />
      <span>{feeImmunityNumericValue}</span>
      {isCompoundingAnnualy ? (
        <CompoundingYieldIcon percentColor="#fff" className="size-4 shrink-0" />
      ) : (
        <span>%</span>
      )}
      <span>p.a.</span>
      <TextTooltip
        text={
          <p className="text-[13px] font-semibold text-primary leading-normal text-wrap">
            Kým ostatní platia poplatky, vy nie.
            <br />
            Celý výnos je váš.
          </p>
        }
        linkText="Zistiť viac"
        linkUrl="/fee-immunity"
        iconClassName="text-white"
      />
    </div>
  )

  const yieldBadge = totalYieldPerAnnum != null && (
    <div className="flex flex-row items-center justify-end text-primary">
      <div
        className="flex flex-row items-center justify-end text-base text-primary h-6 px-2 bg-green font-semibold rounded-md"
        translate="no">
        {formatYieldValue(totalYieldPerAnnum)}
        {isCompoundingAnnualy ? (
          <CompoundingYieldIcon
            size={18}
            arrowColor="var(--color-primary)"
            percentColor="var(--color-primary)"
            className="ml-1 align-middle"
          />
        ) : (
          <>
            % p.a.{' '}
            <RevenueFeeTooltip
              revenueFeeApplied={revenueFeeApplied}
              color="var(--color-primary)"
            />
          </>
        )}
      </div>
    </div>
  )

  return (
    <div className="flex flex-col items-center gap-2 w-full lg:max-w-[375px] min-h-[120px]">
      <div className="self-end flex flex-row items-center gap-2 md:hidden">
        {hasFeeImmunity ? feeImmunityBadge : yieldBadge}
      </div>
      <div
        className={cn(
          'w-full flex items-center gap-2 py-3 px-1 text-[40px] font-semibold leading-[52px] border-b',
          `${usedFutureRevenueFixed && 'border-secondary text-secondary'}`,
          `${(availableFundsExceeded || minimumValueError) && 'border-error text-error'}`
        )}>
        €
        <input
          autoComplete="off"
          className={cn(
            'flex-1 min-w-0 bg-transparent border-0 outline-none font-normal text-primary-foreground',
            `${usedFutureRevenueFixed && 'text-secondary'}`,
            `${(availableFundsExceeded || minimumValueError) && 'text-error'}`
          )}
          {...props}
        />
        <div className="hidden md:flex items-center shrink-0 gap-2">
          {hasFeeImmunity ? feeImmunityBadge : yieldBadge}
        </div>
      </div>

      {availableFundsExceeded ? (
        <Text className="text-xs font-semibold text-error leading-normal">
          Prekračujete dostupné prostriedky,&nbsp;
          <Link
            className="text-xs font-semibold text-error leading-normal no-underline hover:no-underline border-b"
            to="/account/add-funds">
            vložte si peniaze
          </Link>
        </Text>
      ) : (
        minimumValueError != null && (
          <Text className="text-xs font-semibold text-error leading-normal">
            {minimumValueError}
          </Text>
        )
      )}

      {usedFutureRevenueFixed > 0 && !availableFundsExceeded && (
        <Text className="text-xs leading-normal">
          Investujete už očakávané výnosy.{' '}
          <Link
            className="text-xs leading-normal no-underline hover:no-underline border-b"
            to={getLandingPageLink('faq#investicna-cast')}
            target="_blank"
            rel="noopener noreferrer">
            Ako to funguje
          </Link>
          ?
        </Text>
      )}
    </div>
  )
}
